"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Either_1 = require("./Either");
const Util_1 = require("./Util");
const callValidator = (func) => (keyName) => (input) => func.validate(input, keyName);
class Validator {
    constructor(rules) {
        this.rules = rules;
        this.validate = (obj, keyName = '') => {
            if (typeof obj !== 'object') {
                return Either_1.Either.left({
                    correctType: 'object',
                    keyName,
                    message: 'Value received is not an object',
                    valueReceived: obj,
                });
            }
            if (obj === undefined || obj === null) {
                return Either_1.Either.left({
                    correctType: 'object',
                    keyName,
                    message: 'Object cannot be undefined or null',
                    valueReceived: obj,
                });
            }
            const errors = {};
            const returnObject = {};
            let hasError = false;
            for (const key in this.rules) {
                if (this.rules.hasOwnProperty(key)) {
                    const value = obj[key];
                    const rule = this.rules[key];
                    const result = callValidator(rule)(key)(value);
                    if (Either_1.Either.isLeft(result)) {
                        hasError = true;
                        errors[key] = result.value;
                    }
                    else {
                        returnObject[key] = result.value;
                    }
                }
            }
            return hasError
                ? Either_1.Either.left({
                    correctType: 'object',
                    errors,
                    keyName,
                    message: 'Object was unable to be verified',
                    valueReceived: obj,
                })
                : Either_1.Either.right(returnObject);
        };
    }
    static Or(correctType, ...validators) {
        return {
            validate: (input, keyName) => {
                const results = validators.map(callValidator).map(Util_1.call(keyName)).map(Util_1.call(input));
                const errors = results.filter(Either_1.Either.isLeft).map(Util_1.get('value'));
                const whatsLeft = results.filter(Either_1.Either.isRight).map(Util_1.get('value'));
                return errors.length === validators.length - 1
                    ? Either_1.Either.right(whatsLeft[0])
                    : Either_1.Either.left({
                        correctType,
                        message: 'Object did not match one of the required types',
                        keyName,
                        errors,
                        valueReceived: input,
                    });
            },
        };
    }
    static And(correctType, ...validators) {
        return {
            validate: (input, keyName) => {
                const errors = validators
                    .map(callValidator)
                    .map(Util_1.call(keyName))
                    .map(Util_1.call(input))
                    .filter(Either_1.Either.isLeft);
                return errors.length === 0
                    ? Either_1.Either.right(input)
                    : Either_1.Either.left({
                        correctType,
                        keyName,
                        message: 'Object did not match all the required types',
                        errors,
                        valueReceived: input,
                    });
            },
        };
    }
    static OneOfStrict(correctType, ...values) {
        return Validator.Or.apply({}, [correctType, ...values.map(Validator.StrictValue)]);
    }
}
exports.default = Validator;
Validator.Null = {
    validate: (input, keyName) => input === null
        ? Either_1.Either.right(input)
        : Either_1.Either.left({
            keyName,
            correctType: 'null',
            message: `${keyName} must be null`,
            valueReceived: input,
        }),
};
Validator.Anything = {
    validate: Either_1.Either.right,
};
Validator.Number = {
    validate: (input, keyName) => typeof input === 'number'
        ? Either_1.Either.right(input)
        : Either_1.Either.left({
            keyName,
            correctType: 'number',
            message: `${keyName} must be a number`,
            valueReceived: input,
        }),
};
Validator.String = {
    validate: (input, keyName) => typeof input === 'string'
        ? Either_1.Either.right(input)
        : Either_1.Either.left({
            keyName,
            correctType: 'string',
            message: `${keyName} must be a string`,
            valueReceived: input,
        }),
};
Validator.Boolean = {
    validate: (input, keyName) => typeof input === 'boolean'
        ? Either_1.Either.right(input)
        : Either_1.Either.left({
            keyName,
            correctType: 'boolean',
            message: `${keyName} must be a boolean`,
            valueReceived: input,
        }),
};
Validator.Optional = (func) => ({
    validate: (input, keyName) => input === undefined
        ? Either_1.Either.right(undefined)
        : callValidator(func)(keyName)(input),
});
Validator.Required = (validators) => {
    const fields = {};
    for (const field in validators) {
        if (validators.hasOwnProperty(field)) {
            fields[field] = Validator.RequiredPropertyWrapper(validators[field]);
        }
    }
    return new Validator(fields);
};
Validator.Partial = (validators) => ({
    validate: (input, keyName) => {
        if (typeof input !== 'object' || input === null) {
            return Either_1.Either.left({
                keyName,
                correctType: 'Partial<object>',
                message: `${keyName} is null or not an object`,
                valueReceived: input,
            });
        }
        const errors = {};
        const result = {};
        let hasError = false, validator, validatorResult;
        for (const key in validators) {
            if (input.hasOwnProperty(key)) {
                validator = validators[key];
                validatorResult = validator.validate(input[key], key);
                if (Either_1.Either.isLeft(validatorResult)) {
                    hasError = true;
                    errors[key] = validatorResult.value;
                }
                else if (!hasError) {
                    result[key] = validatorResult.value;
                }
            }
        }
        return hasError
            ? Either_1.Either.left({
                keyName,
                correctType: 'Partial<object>',
                message: `${keyName} has invalid properties`,
                valueReceived: input,
                errors,
            })
            : Either_1.Either.right(result);
    },
});
Validator.Enum = (value, enumName) => ({
    validate: (input, keyName) => typeof value[input] !== 'string'
        ? Either_1.Either.left({
            keyName,
            correctType: enumName,
            message: `${keyName} must be an element of the enum '${enumName}'`,
            valueReceived: input,
        })
        : Either_1.Either.right(input),
});
Validator.ArrayOf = (validator) => ({
    validate: (input, keyName) => {
        if (!Array.isArray(input)) {
            return Either_1.Either.left({
                correctType: 'array',
                keyName,
                message: 'object must be an array',
                valueReceived: input,
            });
        }
        const results = input.map(callValidator(validator)(keyName));
        const goodResults = results.filter(Either_1.Either.isRight).map(Util_1.get('value'));
        return goodResults.length === results.length
            ? Either_1.Either.right(goodResults)
            : Either_1.Either.left({
                correctType: 'array',
                errors: results.filter(Either_1.Either.isLeft).map(Util_1.get('value')),
                keyName,
                message: 'object must be an array with properly shaped elements',
                valueReceived: input,
            });
    },
});
Validator.StrictValue = (value) => ({
    validate: (input, keyName) => input === value
        ? Either_1.Either.right(input)
        : Either_1.Either.left({
            correctType: 'const',
            keyName,
            message: `Provided value did not match input value: ${input} vs ${value}`,
            valueReceived: input,
        }),
});
Validator.Values = (valueValidator) => ({
    validate: (input, keyName) => {
        if (input === undefined || input === null) {
            return Either_1.Either.left({
                correctType: 'object',
                keyName,
                message: 'not defined',
                valueReceived: input,
            });
        }
        if (typeof input !== 'object') {
            return Either_1.Either.left({
                correctType: 'object',
                keyName,
                message: 'not an object',
                valueReceived: input,
            });
        }
        const errors = {};
        const newInput = input;
        let hasError = false;
        for (const key in newInput) {
            if (newInput.hasOwnProperty(key)) {
                const obj = newInput[key];
                const result = callValidator(valueValidator)(keyName)(obj);
                if (result.direction === 'left') {
                    errors[key] = result.value;
                    hasError = true;
                }
            }
        }
        return !hasError
            ? Either_1.Either.right(input)
            : Either_1.Either.left({
                correctType: 'object',
                keyName,
                message: 'object properties do not match ',
                errors,
                valueReceived: input,
            });
    },
});
Validator.RequiredPropertyWrapper = (func) => ({
    validate: (input, keyName) => input === undefined
        ? Either_1.Either.left({
            keyName,
            correctType: 'not undefined',
            message: `${keyName} is required`,
            valueReceived: input,
        })
        : func.validate(input, keyName),
});
//# sourceMappingURL=Validator.js.map